// /vms/activity — 帳號 VMS 操作紀錄（ls-loop MV.6，docs/mycam/mv-spec.md ⑥）。
// GET /v3/me/activity?scope=my_devices：主人看得到自己設備上所有人的紀錄（別人顯示遮罩過的 email），
// 被分享的人只看到自己的。分類：即時影像 / 回放 / 設定 / 分享 / 匯出；篩選：時段、攝影機、人。保存 90 天。

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { camZone } from '../../lib/camTz';
import { formatTimestampUs } from '../../lib/format';
import { cameraClass, deviceName } from '../devices/deviceModel';
import { useDeviceList } from '../devices/useDeviceList';
import './vms.css';

export interface ActivityRow {
  action: string;
  category: 'live' | 'playback' | 'settings' | 'share' | 'export';
  device_id?: string;
  actor_user_id?: string;
  actor: string;
  actor_is_me: boolean;
  meta?: Record<string, unknown>;
  ts_us: number;
}

const CATEGORIES = ['all', 'live', 'playback', 'settings', 'share', 'export'] as const;
const RANGES = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 } as const;
type RangeKey = keyof typeof RANGES;


export function activityText(t: (k: string, o?: Record<string, unknown>) => string, r: ActivityRow): string {
  const m = r.meta ?? {};
  switch (r.action) {
    case 'media.session.start':
      return t(m.kind === 'playback' ? 'vmsActivity.playbackStart' : 'vmsActivity.liveStart');
    case 'media.session.end': {
      const d = Number(m.duration_s);
      const dur = Number.isFinite(d) && d >= 0 ? (d >= 60 ? t('vmsActivity.durMin', { m: Math.floor(d / 60), s: d % 60 }) : t('vmsActivity.durSec', { s: d })) : '';
      return t(m.kind === 'playback' ? 'vmsActivity.playbackEnd' : 'vmsActivity.liveEnd', { dur });
    }
    case 'playlist.access':
      // 縮圖（攝影機卡片 / 事件列表）也記一筆：跟真的看錄影分開寫，不然一開列表就像看了一堆錄影
      return t(m.snapshot ? 'vmsActivity.snapshotView' : 'vmsActivity.cloudPlayback');
    case 'device.settings.update':
      return t(m.kind === 'cmd' ? 'vmsActivity.command' : 'vmsActivity.settings', { attr: String(m.attr ?? ''), value: m.value !== undefined ? ` = ${String(m.value)}` : '' });
    case 'recording.settings.changed': {
      const what = ['mode', 'location', 'quality'].filter((k) => m[k] !== undefined).map((k) => `${t(`vmsActivity.rec_${k}`)} ${String(m[k])}`).join('、');
      return t('vmsActivity.recordingSettings', { what });
    }
    case 'device.share':
    case 'device.unshare':
      return t('vmsActivity.share', { after: m.after ? String(m.after) : '' });
    case 'vms.export.create':
      return t('vmsActivity.export', { files: String(m.files ?? ''), id: String(m.export_id ?? '') });
  }
  return r.action;
}

export default function ActivityPage() {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const cameras = useMemo(() => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), [devices]);
  const nameOf = useMemo(() => new Map(cameras.map((d) => [d.device_id, deviceName(d)])), [cameras]);
  const [range, setRange] = useState<RangeKey>('7d');
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>('all');
  const [device, setDevice] = useState('');
  const [actor, setActor] = useState('');
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    const now = Date.now() * 1000;
    apiFetch<ActivityRow[]>('/v3/me/activity', {
      query: { scope: 'my_devices', from: now - RANGES[range] * 86_400_000_000, to: now + 60_000_000, category, devices: device || undefined, actor: actor || undefined, limit: 500 },
    })
      .then((r) => alive && setRows(r.data ?? []))
      .catch((e: unknown) => alive && setError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.networkError')));
    return () => {
      alive = false;
    };
  }, [range, category, device, actor, t]);

  // 人：我 + 這次查到的其他人（遮罩 email）
  const others = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) if (r.actor_user_id && !r.actor_is_me) m.set(r.actor_user_id, r.actor || r.actor_user_id);
    return [...m.entries()];
  }, [rows]);

  return (
    <section className="vms-ac">
      <header className="vms-ec-head">
        <h1 style={{ marginTop: 0 }}>{t('vmsActivity.title')}</h1>
        <Link className="btn-ghost" to="/vms/devices">{t('deviceOverview.title')} →</Link>
      </header>
      <p className="text-secondary vms-ec-sub">{t('vmsActivity.subtitle')}</p>

      <div className="vms-ec-filters card">
        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('eventCenter.range')}</span>
          {(Object.keys(RANGES) as RangeKey[]).map((k) => (
            <button key={k} className={`evt-chip${range === k ? ' active' : ''}`} aria-pressed={range === k} onClick={() => setRange(k)} data-testid={`ac-range-${k}`}>
              {t(`vmsActivity.range_${k}`)}
            </button>
          ))}
        </div>
        <div className="vms-ec-row">
          <span className="vms-ec-row-label text-secondary">{t('vmsActivity.category')}</span>
          {CATEGORIES.map((c) => (
            <button key={c} className={`evt-chip${category === c ? ' active' : ''}`} aria-pressed={category === c} onClick={() => setCategory(c)} data-testid={`ac-cat-${c}`}>
              {t(`vmsActivity.cat_${c}`)}
            </button>
          ))}
        </div>
        <div className="vms-ec-row">
          <label className="vms-ec-row-label text-secondary" htmlFor="ac-dev">{t('eventCenter.cameras')}</label>
          <select id="ac-dev" value={device} onChange={(e) => setDevice(e.target.value)} data-testid="ac-device">
            <option value="">{t('vmsActivity.allCameras')}</option>
            {cameras.map((d) => (
              <option key={d.device_id} value={d.device_id}>{deviceName(d)}</option>
            ))}
          </select>
          <label className="vms-ec-row-label text-secondary" htmlFor="ac-actor">{t('vmsActivity.who')}</label>
          <select id="ac-actor" value={actor} onChange={(e) => setActor(e.target.value)} data-testid="ac-actor">
            <option value="">{t('vmsActivity.everyone')}</option>
            <option value="me">{t('vmsActivity.me')}</option>
            {others.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
        </div>
        <p className="text-secondary vms-lb-msg" style={{ margin: 0 }}>{t('vmsActivity.retention')}</p>
      </div>

      {error && <p className="text-danger" role="alert">{error}</p>}
      {rows === null && !error && <p className="text-secondary">{t('common.loading')}</p>}
      {rows && rows.length === 0 && <p className="text-secondary" data-testid="ac-empty">{t('vmsActivity.empty')}</p>}
      {rows && rows.length > 0 && (
        <div className="vms-ov-scroll card">
          <table className="vms-ov-table" data-testid="ac-table">
            <thead>
              <tr>
                <th>{t('vmsActivity.col_time')}</th>
                <th>{t('vmsActivity.col_camera')}</th>
                <th>{t('vmsActivity.col_who')}</th>
                <th>{t('vmsActivity.col_what')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.ts_us}-${i}`} data-testid="ac-row" data-action={r.action} data-category={r.category}>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatTimestampUs(r.ts_us, { timeZone: r.device_id ? camZone(r.device_id) : undefined })}</td>
                  <td>{r.device_id ? nameOf.get(r.device_id) ?? r.device_id : '—'}</td>
                  <td>{r.actor_is_me ? t('vmsActivity.me') : r.actor || t('vmsActivity.someone')}</td>
                  <td>{activityText(t, r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
